import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useVoyage, voyageStartedOn, type VoyageStop } from '@lb/domain'
import { Trans, useTranslation } from '@lb/i18n'
import { IconBoat, IconChevronRight, IconClose, Skeleton, cn } from '@lb/ui'

import { http } from '../../lib/client'
import { formatCalendarDay } from '../../lib/format'
import { Glyph, VoyageMap, type StopSelection } from './voyage-map'
import { labelFor, stopDestination } from './voyage-stops'

/**
 * `.voyage-hero` — the map, its legend, and the stop you tapped
 * (journey.jsx:194).
 *
 * ── Why the whole hero is behind one query and fails quietly ──────────────
 * The rest of `/journey` — days aboard, the constellation, badges — comes from
 * `GET /journey` and does not need this. So a voyage that will not load takes
 * the map away and leaves the page working, rather than turning the whole
 * screen into an error state over its decoration. This is the one section of
 * the product where "show nothing" is better than "show a retry button".
 */
export function VoyageHero({ joinedLabel }: { joinedLabel?: string }) {
  const { t } = useTranslation()
  const voyage = useVoyage(http)
  const [active, setActive] = useState<StopSelection | null>(null)

  if (voyage.isPending) return <Skeleton className="h-[320px] w-full" />
  if (voyage.isError || !voyage.data) return null

  const { today, totalDays, year } = voyage.data
  /*
   * The map is THIS year aboard (25 Sep 2026): day 1 is `startedOn`, which is
   * the join date only in year one. A member three years in used to see a map
   * of a year long gone with « départ le » their join date on it.
   */
  const startedOn = voyageStartedOn(voyage.data)
  const laterYear = year > 1

  /*
   * Both numbers clamp, and they clamp for different reasons.
   *
   * `today` is not clamped on the wire — a member past their year-mark reports
   * day 400 of 365, and that number is theirs to see. But "113% of the way
   * through the first year" is not a fact about anything, and "-35 days to your
   * year-mark" is worse: it is a countdown running backwards past an event that
   * already happened.
   */
  const percent = Math.min(100, Math.round((today / totalDays) * 100))
  const remaining = Math.max(0, totalDays - today)

  return (
    /*
     * `.voyage-hero` — NOT a card.
     *
     * The heading, the sub-line and the year-mark counter sit directly on the
     * canvas (`journey.css:91`); only the MAP gets a frame, and its frame is a
     * cream-to-sand gradient rather than the flat surface every other card
     * uses. Wrapping the whole thing in one white panel made the biggest
     * heading in the product look like the title of a widget.
     */
    <section className="mb-[72px]">
      {/* `.vh-head` — `align-items: flex-end`, 28px gap, 30px below. */}
      <header className="mb-[30px] flex flex-wrap items-end justify-between gap-7">
        <div className="min-w-0">
          {/* `.vh-eyebrow` — `--slate` at 11.5px with `0.14em` tracking, led
              by a boat glyph in `--ink`. It was terra-coloured throughout at
              11/0.08em, which made it a second accent line competing with the
              numeral in the heading below it. */}
          <p className="mb-3.5 inline-flex items-center gap-2 text-[11.5px] font-semibold uppercase tracking-[0.14em] text-slate">
            <IconBoat size={14} title={null} className="text-ink" />
            {t('voyage.eyebrow', {
              // `joinedLabel` names the JOIN day, so it only fits year one.
              day: (!laterYear && joinedLabel) || formatCalendarDay(startedOn),
            })}
          </p>
          {/* « Année 3 à bord » — only past the first year, where « votre
              année à bord » alone would read as their first. */}
          {laterYear ? (
            <p className="-mt-1.5 mb-3 inline-flex rounded-full bg-cream-dark px-3 py-1 text-[12px] font-semibold text-ink">
              {t('voyage.yearAboard', { year })}
            </p>
          ) : null}
          {/*
           * The page's `h1`, and the day number is emphasised inside it.
           *
           * `journey.jsx:204` sets the numeral in terra within the sentence —
           * "Day **184** of your year aboard." `<Trans>` keeps that one
           * translatable unit instead of three fragments a translator would
           * have to reassemble in the right order for a language that puts them
           * elsewhere.
           */}
          {/*
           * `.vh-head h1` is clamp(36px, 4.6vw, 60px) — this is the biggest
           * type in the product and it should be. `em` is the NUMERIC face,
           * bold and upright (`journey.css:129`), not the serif italic Home
           * uses for a name: one is a figure, the other is a person.
           *
           * `--terra-deep` rather than the design's `--terra`. At this size the
           * threshold is 3:1 for large text and #BF9356 measures 2.91:1 on
           * white — it fails even the relaxed bar. #8A5E27 is the same hue two
           * steps down and clears it.
           */}
          <h1 className="mb-3.5 font-serif text-[clamp(36px,4.6vw,60px)] font-medium leading-[1.02] tracking-[-0.02em] text-ink">
            <Trans
              i18nKey="voyage.title"
              values={{ day: today }}
              components={{ 1: <em className="font-num font-bold not-italic text-ink" /> }}
            />
          </h1>
          {/* `.vh-sub` — 16px/1.6 at 60ch. It was 14.5/1.55, which is the
              body size used for card meta, not for the one paragraph that
              explains the whole screen. */}
          <p className="max-w-[60ch] text-[16px] leading-[1.6] text-muted">
            <Trans
              i18nKey={laterYear ? 'voyage.subLaterYear' : 'voyage.sub'}
              values={{ percent }}
              components={{ 1: <strong className="font-semibold text-ink" /> }}
            />
          </p>
        </div>

        {/* Hidden once the year-mark is past: a "0 days to your year-mark" tile
            is a countdown to something behind you. */}
        {/*
         * `.vh-headstat` — a FILLED dark card, not loose numerals.
         *
         * The design gives this the only ink fill on the screen, which is what
         * makes it read as a counter rather than as another paragraph. As plain
         * text beside a headline it disappeared.
         */}
        {/* `.vh-headstat` — `--r-xl`, `22px 28px`, min 200px wide, the numeral
            at 48px and the label in `--rose`. */}
        {remaining > 0 ? (
          <div className="min-w-[200px] flex-none rounded-[var(--r-xl)] bg-surface-ink px-7 py-[22px] text-center text-on-surface-ink">
            <div className="font-num text-[48px] font-bold leading-none tracking-[-0.02em] tabular-nums">
              {remaining}
            </div>
            <div className="mt-2 text-[11.5px] font-semibold uppercase tracking-[0.08em] text-on-surface-ink opacity-80">
              {t('voyage.toYearMark')}
            </div>
          </div>
        ) : null}
      </header>

      {/* `.vh-mapwrap` — the map panel at `--r-xl`, `28px 28px 18px`. The
          mock's cream-to-sand gradient is gone: the guide paints surfaces flat
          (punch-list item 3), so the panel is amande — `--cream`, which flips
          with the theme — and the map's own marks make it read as a chart. */}
      <div className="rounded-[var(--r-xl)] border border-border bg-cream px-4 pb-[18px] pt-5 sm:px-7 sm:pt-7">
        {/* On a phone the chart keeps a readable width and scrolls sideways
            (9 Oct 2026); squeezed into 300px its stops and labels were specks. */}
        <div className="-mx-1 overflow-x-auto px-1 md:mx-0 md:overflow-visible md:px-0">
          <div className="min-w-[640px] md:min-w-0">
            <VoyageMap voyage={voyage.data} activeId={active?.id ?? null} onSelect={setActive} />
          </div>
        </div>

        {/*
         * The legend draws the map's own marks. Two coloured dots for two
         * categories told a reader nothing — UAT could not say whether
         * « Trainings » meant completed content or « Moments » meant events —
         * and the map never distinguished by colour anyway: it distinguishes
         * by glyph (star, triangle, heart, square) and colours by STATE. So
         * each entry is the glyph on its fill, each carries its definition on
         * hover, and the definitions are readable in full underneath.
         */}
        <ul className="mt-3 flex flex-wrap justify-center gap-x-5 gap-y-2 text-[12px] text-muted">
          {LEGEND.map((entry) => (
            <Legend key={entry.kind} entry={entry} label={t(`voyage.legend.${entry.kind}`)} hint={t(`voyage.legendHint.${entry.kind}`)} />
          ))}
        </ul>
        <details className="mt-2 text-center text-[12px] text-muted">
          <summary className="cursor-pointer underline underline-offset-2">{t('voyage.legendHelp')}</summary>
          <dl className="mx-auto mt-2 grid max-w-[560px] gap-1 text-left">
            {LEGEND.map((entry) => (
              <div key={entry.kind} className="flex gap-2">
                <dt className="flex-none font-semibold text-ink-soft">{t(`voyage.legend.${entry.kind}`)}</dt>
                <dd>{t(`voyage.legendHint.${entry.kind}`)}</dd>
              </div>
            ))}
          </dl>
        </details>
      </div>

      {active ? (
        <ActiveStopCard key={active.id} stops={active.stops} onClose={() => setActive(null)} />
      ) : null}
    </section>
  )
}

/**
 * One entry per mark the map can draw, in the client's own order. `kind`
 * doubles as the i18n key; `glyph` is the map's kind (or null for the two
 * entries that are a state rather than a kind — today, and a challenge's
 * square overlay).
 */
export const LEGEND: readonly {
  kind: 'milestone' | 'course' | 'moment' | 'challenge' | 'today'
  glyph: VoyageStop['kind'] | null
  fill: string
  glyphFill: string
}[] = [
  { kind: 'milestone', glyph: 'milestone', fill: 'var(--ink)', glyphFill: 'var(--cream)' },
  { kind: 'course', glyph: 'course', fill: 'var(--terra)', glyphFill: 'var(--ink-on-tint)' },
  { kind: 'moment', glyph: 'moment', fill: 'var(--terra)', glyphFill: 'var(--ink-on-tint)' },
  { kind: 'challenge', glyph: null, fill: 'var(--terra)', glyphFill: 'var(--ink)' },
  { kind: 'today', glyph: null, fill: 'var(--vert)', glyphFill: 'var(--ink-on-tint)' },
]

function Legend({ entry, label, hint }: { entry: (typeof LEGEND)[number]; label: string; hint: string }) {
  return (
    <li className="flex items-center gap-1.5" title={hint}>
      <svg width="16" height="16" viewBox="-8 -8 16 16" aria-hidden className="flex-none">
        {entry.kind === 'today' ? (
          <circle r="7" fill="none" stroke="var(--vert)" strokeWidth="1" opacity="0.55" />
        ) : null}
        <circle r="5" fill={entry.fill} stroke="var(--ink)" strokeWidth="1" />
        {entry.glyph ? <Glyph kind={entry.glyph} fill={entry.glyphFill} /> : null}
        {entry.kind === 'challenge' ? <rect x="-2.5" y="-2.5" width="5" height="5" fill={entry.glyphFill} opacity="0.6" /> : null}
      </svg>
      {label}
    </li>
  )
}

/**
 * `.active-stop-card` — what a stop is, once you tap it (journey.jsx:386).
 *
 * `role="status"`, because tapping a dot in an SVG changes a region somewhere
 * else on the page. Without it the map is a set of controls whose effect is
 * invisible to anybody not looking at that spot.
 *
 * The body copy is chosen by `state`, per the design: done, here, ahead. The
 * "ahead" line is the one worth keeping exactly — *"Ahead. We'll get there. No
 * pressure."* is the whole tone of the product in six words, and a neutral
 * "Upcoming" would throw it away.
 *
 * ── One tap target can be several stops ───────────────────────────────────
 * Stops too close to tap apart share a target (`clusterStops`), so the card
 * lists every stop in it, in day order, each with its own « Open ». One stop
 * reads exactly as it always did.
 *
 * ── « Open » ──────────────────────────────────────────────────────────────
 * The acceptance line was "each stop opens its content or detail view". A
 * stop whose `stopDestination` is null — a milestone, a storm — has nothing
 * behind it and shows no link rather than a dead one.
 */
function ActiveStopCard({ stops, onClose }: { stops: VoyageStop[]; onClose: () => void }) {
  const { t } = useTranslation()
  const single = stops.length === 1 ? stops[0] : undefined

  return (
    <div
      role="status"
      className="relative mt-5 flex items-start gap-4 rounded-[var(--r)] border border-border bg-cream-dark p-card"
    >
      {single ? (
        <>
          <StateDot stop={single} />
          <div className="min-w-0 flex-1">
            <StopBody stop={single} heading="h3" />
          </div>
        </>
      ) : (
        <div className="min-w-0 flex-1">
          <h3 className="font-serif text-[19px] font-medium text-ink">
            {t('voyage.clusterTitle', { count: stops.length })}
          </h3>
          <ul className="mt-3 flex flex-col divide-y divide-border">
            {stops.map((stop, index) => (
              <li key={`${stop.day}-${stop.key}-${index}`} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
                <StateDot stop={stop} />
                <div className="min-w-0 flex-1">
                  <StopBody stop={stop} heading="h4" />
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <button
        type="button"
        onClick={onClose}
        aria-label={t('voyage.close')}
        className="-me-1.5 -mt-1.5 grid h-11 w-11 flex-none place-items-center rounded-full text-muted transition-colors hover:bg-surface hover:text-ink"
      >
        <IconClose size={14} title={null} />
      </button>
    </div>
  )
}

function StateDot({ stop }: { stop: VoyageStop }) {
  return (
    <span
      aria-hidden
      className={cn(
        'mt-1 h-3.5 w-3.5 flex-none rounded-full',
        stop.state === 'upcoming' ? 'border-2 border-dashed border-slate' : 'bg-ink',
      )}
    />
  )
}

function StopBody({ stop, heading: Heading }: { stop: VoyageStop; heading: 'h3' | 'h4' }) {
  const { t } = useTranslation()
  const times = stop.count ?? 1

  return (
    <>
      <p className="text-[12px] font-semibold uppercase tracking-[0.06em] text-slate">
        {t('voyage.day', { day: stop.day })} · {t(`voyage.kind.${stop.kind}`)}
      </p>
      <Heading
        className={cn(
          'mt-1 font-serif font-medium text-ink',
          Heading === 'h3' ? 'text-[19px]' : 'text-[16.5px]',
        )}
      >
        {labelFor(stop, t)}
      </Heading>
      {/* `count` — the same key on the same day, collapsed by the server. */}
      {times > 1 ? (
        <p className="mt-0.5 text-[13px] text-ink-soft">{t('voyage.times', { count: times })}</p>
      ) : null}
      <p className="mt-1.5 text-[14px] leading-[1.5] text-muted">{stateLine(stop, t)}</p>
      <OpenStop stop={stop} />
    </>
  )
}

/**
 * The sentence under a stop. A done stop with somewhere to go says it can be
 * revisited — the « Open » under it is how; one with nowhere to go (a
 * milestone, a storm) no longer promises a tap that does nothing.
 */
function stateLine(stop: VoyageStop, t: (key: string) => string): string {
  if (stop.state === 'done' && !stopDestination(stop)) return t('voyage.state.doneStatic')
  return t(`voyage.state.${stop.state}`)
}

/** The way into what a stop is about — see `stopDestination`. */
function OpenStop({ stop }: { stop: VoyageStop }) {
  const { t } = useTranslation()
  const destination = stopDestination(stop)
  if (!destination) return null

  const className =
    'mt-3 inline-flex min-h-[44px] items-center gap-1 rounded-full border border-border bg-surface px-4 text-[13.5px] font-semibold text-ink transition-colors hover:border-ink-soft'
  const inner = (label: string) => (
    <>
      {label}
      <IconChevronRight size={14} title={null} />
    </>
  )

  switch (destination.kind) {
    case 'course':
      return (
        <Link to="/resources/courses/$id" params={{ id: destination.id }} className={className}>
          {inner(t('voyage.open.course'))}
        </Link>
      )
    case 'thread':
      return (
        <Link
          to="/community/$roomId/posts/$postId"
          params={{ roomId: destination.roomId, postId: destination.postId }}
          className={className}
        >
          {inner(t('voyage.open.thread'))}
        </Link>
      )
    case 'content':
      return (
        <Link to={destination.to} className={className}>
          {inner(t('voyage.open.content'))}
        </Link>
      )
    case 'challenge':
      return (
        <Link to="/challenge" className={className}>
          {inner(t('voyage.open.challenge'))}
        </Link>
      )
  }
}
